/**
 * SettingsCategoryPage
 * Flutter source: lib/presentation/screens/settings/category/settings_category_screen.dart
 *
 * Displays individual toggle settings for a given category.
 * Category is determined from the route param `:id`.
 */

import { useRef, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { FlexibleHeaderBackground } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderBackground';
import { UserSettingCategory } from '@/domain/enums/settings/userSettingCategory';
import type { UserSettingStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { useSettingsListItems } from '../hooks/useSettingsListItems';
import { useUserSettings } from './hooks/useUserSettings';
import { SettingToggleItem } from './components/SettingToggleItem';

export const SettingsCategoryPage = () => {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const location = useLocation();

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname)
  );
  const screenKey = screenKeyRef.current;

  const category = Number(id) as UserSettingCategory;

  const listItems = useSettingsListItems();
  const categoryInfo = useMemo(() => {
    const found = listItems.find((item) => item.category === category);
    return {
      title: found?.title ?? l10n.settings.title,
      iconPath: found?.iconPath ?? '/assets/images/settings.png',
    };
  }, [category, listItems, l10n.settings.title]);

  const {
    settings,
    isLoading,
    isToggling,
    toggleSetting,
  } = useUserSettings(category);

  useBasePage({
    screenKey,
  });

  const renderSettingItem = useCallback((setting: UserSettingStorage) => (
    <div style={{ padding: `0 ${responsive.spacing.lg}px` }}>
      <SettingToggleItem
        setting={setting}
        isToggling={isToggling[setting.name] ?? false}
        onToggle={() => toggleSetting(setting.name)}
      />
    </div>
  ), [responsive.spacing.lg, isToggling, toggleSetting]);

  return (
    <FlexibleHeaderList
      qa={{ id: 'settings-category', status: isLoading ? 'loading' : 'ready' }}
      listQa={{ id: 'settings-category-list' }}
      items={settings}
      renderItem={renderSettingItem}
      getItemKey={(setting) => setting.name}
      hasMore={false}
      onLoadMore={() => { }}
      backgroundColor={theme.background}
      emptyListComponent={
        isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: `${responsive.spacing['3xl']}px 0` }}>
            <ExpauseLoadingIndicator />
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: `${responsive.spacing['3xl']}px 0`, color: theme.text.secondary, fontSize: responsive.font.base }}>
            {l10n.settings.noSettings}
          </div>
        )
      }
      appBarOptions={{
        title: categoryInfo.title,
        onLeadingTap: () => Navigator.goBack(),
        backgroundColor: theme.background,
        flexibleSpace: {
          parallax: true,
          background: (
            <FlexibleHeaderBackground
              // iconPath={categoryInfo.iconPath}
              title={categoryInfo.title}
            />
          ),
        },
      }}
    />
  );
};
